import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface Doctor {
  id: string;
  name: string;
  specialization_id: string;
  location: string;
  contact_number?: string;
  email?: string;
  program_ids: string[];
  hq_id?: string;
  hq_name?: string;
  zone_id?: string;
  state?: string;
  district?: string;
  city?: string;
  pincode?: string;
  address?: string;
  is_active: boolean;
  organization_id: string;
  created_at: string;
  updated_at: string;
}

export interface CreateDoctorRequest {
  name: string;
  specialization_id: string;
  location: string;
  contact_number?: string;
  email?: string;
  program_ids?: string[];
  hq_id?: string;
  hq_name?: string;
  zone_id?: string;
  state?: string;
  district?: string;
  city?: string;
  pincode?: string;
  address?: string;
}

export interface UpdateDoctorRequest {
  name?: string;
  specialization_id?: string;
  location?: string;
  contact_number?: string;
  email?: string;
  program_ids?: string[];
  hq_id?: string;
  hq_name?: string;
  zone_id?: string;
  state?: string;
  district?: string;
  city?: string;
  pincode?: string;
  address?: string;
  is_active?: boolean;
}

export interface DoctorsResponse {
  success: boolean;
  data: {
    doctors: Doctor[];
    totalPages: number;
    currentPage: number;
    totalDoctors: number;
  };
}

export interface ProgramInfo {
  id: string;
  program_id: string;
  program_name: string;
  event_type?: string;
  location?: string;
  date?: string;
}

export interface DoctorOverview {
  id: string;
  doctor_id: string;
  name: string;
  specialization: string;
  location: string;
  status: string;
  associated_programs: number;
  programs: ProgramInfo[];
  total_patients: number;
  active_patients: number;
  high_risk_patients: number;
  total_sales: number;
  average_order_value: number;
  programs_involved: number;
  available_locations: string[];
  available_programs: ProgramInfo[];
}

export interface Telecaller {
  id: string;
  user_id: string;
  name: string;
  avatar_image: string;
}

export interface PatientTimeline {
  date: string;
  information: string;
}

export interface DoctorPatient {
  name: string;
  email: string;
  age: number;
  gender: string;
  contact_number: string;
  program_ids: string[];
  status: string;
  followup_status: string;
  therapy_status: string;
  risk_level: string;
  followups_completed: number;
  followups_pending: number;
  followups_rescheduled: number;
  next_followup_date: string;
  last_followup_date: string;
  next_reminder_date: string;
  therapy_start_date: string;
  completion_rate: number;
  award_points: number;
  consent_status: string;
  consent_channels: string[];
  therapy_notes: string;
  treatment_notes: string;
  diagnostics_notes: string;
  timeline: PatientTimeline[];
  id: string;
  patient_id: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  user_id: string;
  doctor_id: string;
  telecaller: Telecaller;
  calls_count: number;
  called_by_telecallers: string[];
  programs: ProgramInfo[];
}

export interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  limit: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface DoctorPatientsStats {
  total_patients: number;
  active_patients: number;
  high_risk_patients: number;
  followups_due_this_week: number;
  followups_completed: number;
  followups_pending: number;
  followups_rescheduled: number;
  total_followups_done: number;
}

export interface DoctorPatientsResponse {
  patients: DoctorPatient[];
  programs: ProgramInfo[];
  telecallers: Telecaller[];
  pagination: Pagination;
  stats: DoctorPatientsStats;
}

export interface PurchaseItem {
  name: string;
  company: string;
  quantity: number;
  rate: number;
  discount: number;
  duration: number;
  amount: number;
  total: number;
  item_type: string;
}

export interface Purchase {
  patient_id: string;
  purchase_date: string;
  sold_by: string;
  doctor_id: string;
  hospital_id: string;
  lab_id: string;
  description: string;
  id: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  medicines: PurchaseItem[];
  lab_tests: PurchaseItem[];
  total_amount: number;
  total_discount: number;
  net_total: number;
}

export interface SalesReportStats {
  total_purchases: number;
  total_amount: number;
  total_net_amount: number;
}

export interface DoctorSalesReportResponse {
  purchases: Purchase[];
  pagination: Pagination;
  stats: SalesReportStats;
}

export interface AnalyticsDataPoint {
  date: string;
  value: number;
}

export interface AnalyticsGraph {
  title: string;
  data_points: AnalyticsDataPoint[];
  total: number;
}

export interface AnalyticsSummary {
  total_patients: number;
  active_patients: number;
  total_sales: number;
  programs_involved: number;
}

export interface DoctorAnalyticsResponse {
  doctor_id: string;
  doctor_name: string;
  member_since: string;
  summary: AnalyticsSummary;
  patients_graph: AnalyticsGraph;
  sales_graph: AnalyticsGraph;
  start_date: string;
  end_date: string;
}

@Injectable({
  providedIn: 'root',
})
export class DoctorsApiService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get doctors with pagination and optional search
   * GET /api/v1/doctors/
   */
  getDoctors(
    page: number = 1,
    limit: number = 10,
    search?: string,
    hq?: string,
    city?: string,
    state?: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) params = params.set('search', search);
    if (hq) params = params.set('hq', hq);
    if (city) params = params.set('city', city);
    if (state) params = params.set('state', state);

    return this.http.get<any>(`${this.apiUrl}/doctors/`, { params });
  }

  /**
   * Get a specific doctor by ID
   * GET /api/v1/doctors/{doctor_id}
   */
  getDoctor(id: string): Observable<Doctor> {
    return this.http.get<Doctor>(`${this.apiUrl}/doctors/${id}`);
  }

  /**
   * Create a new doctor
   * POST /api/v1/doctors/
   */
  createDoctor(doctorData: CreateDoctorRequest): Observable<Doctor> {
    const payload = { ...doctorData };
    if (!payload.location || !payload.location.trim()) {
      const parts = [payload.city, payload.district, payload.state, payload.pincode]
        .map((p) => (p ? String(p).trim() : ''))
        .filter(Boolean);
      if (parts.length > 0) {
        payload.location = parts.join(', ');
      }
    }
    if ('hq_id' in payload) {
      delete payload.hq_id;
    }
    return this.http.post<Doctor>(`${this.apiUrl}/doctors/`, payload);
  }

  /**
   * Bulk upload doctors via CSV/Excel
   * POST /api/v1/doctors/bulk-upload
   */
  bulkUploadDoctors(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${this.apiUrl}/doctors/bulk-upload`, formData);
  }

  /**
   * Update an existing doctor
   * PATCH /api/v1/doctors/{doctor_id}
   */
  updateDoctor(id: string, doctorData: UpdateDoctorRequest): Observable<Doctor> {
    const payload = { ...doctorData };
    if (!payload.location || !payload.location.trim()) {
      const parts = [payload.city, payload.district, payload.state, payload.pincode]
        .map((p) => (p ? String(p).trim() : ''))
        .filter(Boolean);
      if (parts.length > 0) {
        payload.location = parts.join(', ');
      }
    }
    if ('hq_id' in payload) {
      delete payload.hq_id;
    }
    return this.http.patch<Doctor>(`${this.apiUrl}/doctors/${id}`, payload);
  }

  /**
   * Delete a doctor
   * DELETE /api/v1/doctors/{doctor_id}
   */
  deleteDoctor(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/doctors/${id}`);
  }

  /**
   * Change doctor status (active/inactive)
   * PATCH /api/v1/doctors/{doctor_id}/status
   */
  changeDoctorStatus(id: string, status: string): Observable<Doctor> {
    return this.http.patch<Doctor>(`${this.apiUrl}/doctors/${id}/status`, { status });
  }

  /**
   * Get doctor overview with metrics
   * GET /api/v1/doctors/{doctor_id}/overview
   */
  getDoctorOverview(
    doctorId: string,
    startDate?: string,
    endDate?: string,
    location?: string,
    programId?: string
  ): Observable<DoctorOverview> {
    let url = `${this.apiUrl}/doctors/${doctorId}/overview`;
    const params: string[] = [];

    if (startDate) params.push(`start_date=${encodeURIComponent(startDate)}`);
    if (endDate) params.push(`end_date=${encodeURIComponent(endDate)}`);
    if (location) params.push(`location=${encodeURIComponent(location)}`);
    if (programId) params.push(`program_id=${encodeURIComponent(programId)}`);

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http.get<DoctorOverview>(url);
  }

  /**
   * Get patients for a specific doctor
   * GET /api/v1/doctors/{doctor_id}/patients
   */
  getDoctorPatients(
    doctorId: string,
    search?: string,
    page: number = 1,
    limit: number = 10
  ): Observable<DoctorPatientsResponse> {
    let url = `${this.apiUrl}/doctors/${doctorId}/patients?page=${page}&limit=${limit}`;

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    return this.http.get<DoctorPatientsResponse>(url);
  }

  /**
   * Get sales report for a specific doctor
   * GET /api/v1/doctors/{doctor_id}/sales-report
   */
  getDoctorSalesReport(
    doctorId: string,
    params?: {
      item_name?: string;
      start_date?: string;
      end_date?: string;
      patient_id?: string;
      last_days?: number;
      type?: 'medicine' | 'lab';
      page?: number;
      limit?: number;
    }
  ): Observable<DoctorSalesReportResponse> {
    let url = `${this.apiUrl}/doctors/${doctorId}/sales-report`;
    const queryParams: string[] = [];

    if (params) {
      if (params.item_name) queryParams.push(`item_name=${encodeURIComponent(params.item_name)}`);
      if (params.start_date) queryParams.push(`start_date=${encodeURIComponent(params.start_date)}`);
      if (params.end_date) queryParams.push(`end_date=${encodeURIComponent(params.end_date)}`);
      if (params.patient_id) queryParams.push(`patient_id=${encodeURIComponent(params.patient_id)}`);
      if (params.last_days) queryParams.push(`last_days=${params.last_days}`);
      if (params.type) queryParams.push(`type=${params.type}`);
      if (params.page) queryParams.push(`page=${params.page}`);
      if (params.limit) queryParams.push(`limit=${params.limit}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return this.http.get<DoctorSalesReportResponse>(url);
  }

  /**
   * Get doctor analytics
   * GET /api/v1/dashboard/analytics/{doctor_id}
   */
  getDoctorAnalytics(doctorId: string, startDate?: string, endDate?: string): Observable<DoctorAnalyticsResponse> {
    let params: any = {};
    
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    return this.http.get<DoctorAnalyticsResponse>(
      `${this.apiUrl}/dashboard/analytics/${doctorId}`,
      { params }
    );
  }
}
